"use client";

import { User } from "lucide-react";
import * as React from "react";

import { useStaticGifObjectUrl } from "@/shared/lib/hooks/use-static-gif-object-url";
import {
  DEFAULT_AVATAR_URL,
  getAvatarUrl,
  handleImageError,
  isLikelyAnimatedGifUrl,
} from "@/shared/lib/image-utils";
import { cn } from "@/shared/lib/utils";

import { Avatar, AvatarImage, AvatarFallback } from "./avatar";

interface SafeAvatarProps {
  src?: string | null;
  alt?: string;
  fallback?: React.ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  allowAnimatedGif?: boolean;
}

export function SafeAvatar({
  src,
  alt = "Avatar",
  fallback,
  className = "",
  size = "md",
  allowAnimatedGif = false,
}: SafeAvatarProps) {
  const [imageError, setImageError] = React.useState(false);
  const safeSrc = getAvatarUrl(src);

  const wantsStaticGif =
    !allowAnimatedGif &&
    !imageError &&
    Boolean(safeSrc) &&
    isLikelyAnimatedGifUrl(safeSrc) &&
    safeSrc !== DEFAULT_AVATAR_URL;

  const { url: staticGifUrl, pending: staticGifPending } =
    useStaticGifObjectUrl(safeSrc, wantsStaticGif);

  const sizeClasses = {
    sm: "size-6",
    md: "size-8",
    lg: "size-12",
    xl: "size-16",
  };

  const handleError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    setImageError(true);
    handleImageError(event);
  };

  const defaultFallback = (
    <div className="flex items-center justify-center w-full h-full bg-muted rounded-full">
      <User className="h-1/2 w-1/2 text-muted-foreground" />
    </div>
  );

  const useGifImg =
    allowAnimatedGif &&
    !imageError &&
    Boolean(safeSrc) &&
    isLikelyAnimatedGifUrl(safeSrc) &&
    safeSrc !== DEFAULT_AVATAR_URL;

  if (useGifImg) {
    return (
      <div
        className={cn(
          "relative flex shrink-0 overflow-hidden rounded-full",
          sizeClasses[size],
          className,
        )}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError это событие загрузки картинки, а не взаимодействие пользователя */}
        <img
          src={safeSrc}
          alt={alt}
          className="aspect-square size-full object-cover"
          onError={handleError}
        />
      </div>
    );
  }

  if (wantsStaticGif && staticGifPending) {
    return (
      <Avatar className={`${sizeClasses[size]} ${className}`}>
        <AvatarFallback>{fallback || defaultFallback}</AvatarFallback>
      </Avatar>
    );
  }

  const radixSrc = wantsStaticGif ? (staticGifUrl ?? safeSrc) : safeSrc;

  return (
    <Avatar className={`${sizeClasses[size]} ${className}`}>
      {!imageError && radixSrc && (
        <AvatarImage src={radixSrc} alt={alt} onError={handleError} />
      )}
      <AvatarFallback>{fallback || defaultFallback}</AvatarFallback>
    </Avatar>
  );
}
